import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'app/AppStore';
import Filter from 'components/Filter';
import { FiltersRange, getIamSearchResultFacetMap, TextFacetLocal } from 'components/Filter/Filter.types';
import { FilterHelper } from 'components/Filter/FilterHelper';
import { useFetchBamCommercialFamilyCodes } from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.requests';
import {
  createIAMSupplierReferenceKey,
  IAMFulltextSearchResult,
  ReferenceWithSupplier,
} from 'domains/catalog/Catalog.types';
import { getIAMReferences, IAMReferenceLocal } from 'domains/references';
import { getSparePartsView } from 'domains/user';
import SearchResultSection from 'pages/CatalogPage/IAM/FullTextSearchResult/SearchResultSection';
import { useSearchResultLabel } from 'pages/CatalogPage/IAM/FullTextSearchResult/utils';
import { Container, defaultPadding, Flex, InfiniteScroll, MarginBox, Text, useInfiniteScroll } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { useExtraLarge } from 'utils';
import { useResetScrollSharp } from 'utils/hooks/useResetScroll';

const PAGING_SIZE = 10;

interface SearchResultContainerProps {
  searchResult: IAMFulltextSearchResult[];
}

const Filters = {
  Supplier: {
    id: 'supplierLabel',
    groupFn: (refDetail: IAMReferenceLocal[]) => FilterHelper.calculateFrequencies(refDetail, 'supplier'),
    filterFn: (refDetail: IAMReferenceLocal[], toMatch: string[] | undefined) =>
      FilterHelper.filterData(refDetail, 'supplier', toMatch),
  },
  Family: {
    id: 'familyLabel',
    groupFn: (refDetail: IAMReferenceLocal[]) => FilterHelper.calculateFrequencies(refDetail, 'families.label'),
    filterFn: (refDetail: IAMReferenceLocal[], toMatch: string[] | undefined) =>
      FilterHelper.filterData(refDetail, 'families.label', toMatch),
  },
} as const;

const findRefDetailByNumber = (ref: ReferenceWithSupplier, allRefDetails: IAMReferenceLocal[]) =>
  allRefDetails.find(
    (detail) => detail.referenceNumber === ref.referenceNumber && detail.supplierCode === ref.supplierCode,
  );

const SearchResultContainer = ({ searchResult }: SearchResultContainerProps) => {
  const { t } = useTranslation();
  const xLarge = useExtraLarge();
  const [activeTextFilters, setActiveTextFilters] = useState<Map<string, string[]>>(new Map<string, string[]>());
  const [displayedTextFilters, setDisplayedTextFilters] = useState<TextFacetLocal[]>([]);
  const [filteredData, setFilteredData] = useState<IAMFulltextSearchResult[]>([]);

  useFetchBamCommercialFamilyCodes();
  const referenceNumbers = useMemo(
    () =>
      searchResult
        .flatMap((result) => result.mainReferences || [])
        .map((r) => createIAMSupplierReferenceKey(r.referenceNumber, r.supplierCode)),
    [searchResult],
  );
  let iamVehicleKey: string | undefined;
  const allRefDetails = useSelector((state: RootState) =>
    getIAMReferences(state, {
      vehicleKey: iamVehicleKey,
      referenceNumbers,
    }),
  );

  const sparePartsView = useSelector(getSparePartsView);
  const { label } = useParams<{
    query: string;
    label: string;
  }>();

  useEffect(() => {
    const displayedSearchResult = searchResult.filter(
      (result) =>
        (result.mainReferences && result.mainReferences.length > 0) ||
        (result.equivalentReferences && result.equivalentReferences.length > 0),
    );

    const refNumber = displayedSearchResult.flatMap((result) => result.mainReferences || []);
    const refDetail = refNumber
      .map((item) => findRefDetailByNumber(item, allRefDetails))
      .filter((i): i is IAMReferenceLocal => i !== undefined);

    const loadedFilters = Object.entries(Filters).map(([_, filter], index) => ({
      active: true,
      order: index,
      id: filter.id,
      type: 'TEXT',
      items: filter.groupFn(refDetail),
    })) as TextFacetLocal[];

    setDisplayedTextFilters(loadedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult]);

  useEffect(() => {
    const displayedSearchResult = searchResult.filter(
      (result) =>
        (result.mainReferences && result.mainReferences.length > 0) ||
        (result.equivalentReferences && result.equivalentReferences.length > 0),
    );

    const filteredToDisplay = displayedSearchResult.filter((i) => {
      if (!activeTextFilters.size) {
        return true;
      }

      const refBrands = i.mainReferences?.flatMap((r) => findRefDetailByNumber(r, allRefDetails));

      const remainingData = Object.values(Filters).reduce(
        (previousData, filter) => filter.filterFn(previousData, activeTextFilters.get(filter.id)),
        refBrands as IAMReferenceLocal[],
      );

      return remainingData.length > 0;
    });

    setFilteredData(filteredToDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTextFilters, searchResult]);

  const { currentData, hasMore, loadMore } = useInfiniteScroll<IAMFulltextSearchResult>({
    data: filteredData ?? [],
    chunkSize: PAGING_SIZE,
  });

  const handleSetTextFilters = useCallback((id: string, item: string) => {
    setActiveTextFilters((prevFilters) => FilterHelper.updateTextFilter(id, item, prevFilters));
  }, []);

  const handleResetFilters = useCallback(() => {
    setActiveTextFilters(new Map<string, string[]>());
  }, []);

  useResetScrollSharp();

  const searchResultLabel = useSearchResultLabel(filteredData.length, label);

  return (
    <Container padding={defaultPadding}>
      <MarginBox mt={60}>
        <Flex direction={'row'} align={'baseline'} gap={5}>
          <Text type={'h3_dim'} noWrap ellipsis>
            {searchResultLabel}
          </Text>
        </Flex>
        <MarginBox mt={5} />
        <ViewTabs />
        <MarginBox mt={20} />
        <Flex gap={15} direction={xLarge ? 'row' : 'column'}>
          <Flex direction={xLarge ? 'column' : 'row'} size={0} justify={xLarge ? 'flex-start' : 'flex-end'}>
            <MarginBox mt={10}>
              <Filter
                filters={{ rangeFilters: new Map<string, FiltersRange>(), textFilters: activeTextFilters }}
                textFilters={displayedTextFilters}
                rangeFilters={undefined}
                setTextFilters={handleSetTextFilters}
                resetFilters={handleResetFilters}
                loading={false}
                facetsMap={getIamSearchResultFacetMap(t)}
                displayWhenEmpty={false}
              />
            </MarginBox>
          </Flex>
          <Flex minWidth={0} overflowX={'hidden'} direction={'column'}>
            <InfiniteScroll hasMore={hasMore} loadMore={loadMore}>
              <Flex gap={20} direction={'column'}>
                {currentData.map((group: IAMFulltextSearchResult) => (
                  <SearchResultSection
                    key={group.groupName}
                    searchResultGroup={group}
                    sparePartsView={sparePartsView}
                  />
                ))}
              </Flex>
            </InfiniteScroll>
          </Flex>
        </Flex>
      </MarginBox>
    </Container>
  );
};

export default SearchResultContainer;
